import * as React from 'react';

import { cn } from '../lib/utils';
import {
  ChartDataTable,
  summarizeSeries,
  type ChartTableSeries,
} from './charts/chart-data-table';

export interface LineChartSeries {
  name: string;
  data: number[];
  /** CSS color string. Defaults to chart palette slots 1-5. */
  color?: string;
}

export interface LineChartProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Multi-series data */
  series: LineChartSeries[];
  /** X-axis labels (usually timestamps or periods) */
  labels?: string[];
  /** Height in pixels */
  height?: number;
  /** Show gridlines */
  showGrid?: boolean;
  /** Show legend */
  showLegend?: boolean;
  /** Optional control-limit overlays (for SPC charts) */
  controlLimits?: {
    ucl?: number;
    lcl?: number;
    mean?: number;
  };
}

const DEFAULT_PALETTE = [
  'hsl(var(--primary))',
  'hsl(var(--state-running))',
  'hsl(var(--alarm-minor))',
  'hsl(var(--alarm-advisory))',
  'hsl(var(--state-maintenance))',
];

/**
 * Lightweight SVG-based line chart using design tokens.
 * Supports multi-series, control limits (for SPC), gridlines, and legend.
 * Responsive — scales to container width.
 *
 * @example
 * ```tsx
 * <LineChart
 *   series={[{ name: 'OEE', data: [72, 75, 80, 82, 85] }]}
 *   labels={['Mon','Tue','Wed','Thu','Fri']}
 *   height={200}
 * />
 * ```
 */
export const LineChart = React.forwardRef<HTMLDivElement, LineChartProps>(
  (
    {
      series,
      labels,
      height = 200,
      showGrid = true,
      showLegend = true,
      controlLimits,
      className,
      ...props
    },
    ref
  ) => {
    const width = 600;
    const padding = { top: 20, right: 20, bottom: 40, left: 40 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Compute global min/max across series. Guard against empty series so
    // that an empty chart doesn't produce NaN axis coordinates downstream
    // (Math.min(...[]) is +Infinity, which propagates through every yFor()
    // call and lands in the DOM as `<line y1="NaN">` warnings).
    const allValues = series.flatMap((s) => s.data).filter((v) => Number.isFinite(v));
    if (Number.isFinite(controlLimits?.ucl as number)) allValues.push(controlLimits!.ucl as number);
    if (Number.isFinite(controlLimits?.lcl as number)) allValues.push(controlLimits!.lcl as number);
    if (Number.isFinite(controlLimits?.mean as number)) allValues.push(controlLimits!.mean as number);
    const hasData = allValues.length > 0;
    const min = hasData ? Math.min(...allValues) : 0;
    const max = hasData ? Math.max(...allValues) : 1;
    const range = max - min || 1;
    const valueRange = range * 1.1; // 10% headroom
    const valueMin = min - range * 0.05;

    const xStep = series[0]?.data.length
      ? chartWidth / (series[0].data.length - 1 || 1)
      : 0;

    const pointsFor = (data: number[]) =>
      data
        .map((v, i) => {
          const x = padding.left + i * xStep;
          const y = padding.top + (1 - (v - valueMin) / valueRange) * chartHeight;
          return `${x},${y}`;
        })
        .join(' ');

    // ── Textual equivalent (WCAG 1.1.1) ────────────────────────────────
    // Derived from the same `series`/`labels` the SVG draws. Points with no
    // finite value become an explicit gap rather than an implied zero, so an
    // audit reader can tell "not measured" from "measured zero".
    const tableSeries: ChartTableSeries[] = series.map((s) => ({
      id: s.name,
      label: s.name,
      points: s.data.map((v, i) => ({
        label: labels?.[i] ?? `#${i + 1}`,
        value: Number.isFinite(v) ? v : null,
      })),
    }));

    const yFor = (v: number) => {
      // Belt-and-braces: even with the empty-series guard above, callers can
      // pass undefined / NaN control limits. Clamp to chart center so the
      // SVG always gets a real number.
      if (!Number.isFinite(v)) return padding.top + chartHeight / 2;
      return padding.top + (1 - (v - valueMin) / valueRange) * chartHeight;
    };

    // Gridlines (5 horizontal)
    const gridLines = Array.from({ length: 5 }, (_, i) => {
      const y = padding.top + (i / 4) * chartHeight;
      const value = valueMin + (1 - i / 4) * valueRange;
      return { y, value };
    });

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          data-slot="chart-canvas"
          role="img"
          /* "Line chart" named the picture and conveyed none of it. The
           * summary carries the shape; `ChartDataTable` below carries the
           * values, and prints. */
          aria-label={summarizeSeries('Line chart', tableSeries)}
        >
          {/* Gridlines */}
          {showGrid &&
            gridLines.map((line, i) => (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={line.y}
                  x2={width - padding.right}
                  y2={line.y}
                  stroke="hsl(var(--border))"
                  strokeWidth={1}
                  strokeDasharray="2 2"
                />
                <text
                  x={padding.left - 6}
                  y={line.y + 3}
                  className="fill-muted-foreground"
                  style={{ fontSize: 10 }}
                  textAnchor="end"
                >
                  {line.value.toFixed(1)}
                </text>
              </g>
            ))}

          {/* Control limits (SPC) */}
          {controlLimits?.ucl != null && (
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yFor(controlLimits.ucl)}
              y2={yFor(controlLimits.ucl)}
              stroke="hsl(var(--alarm-critical))"
              strokeWidth={1.5}
              strokeDasharray="4 4"
            />
          )}
          {controlLimits?.lcl != null && (
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yFor(controlLimits.lcl)}
              y2={yFor(controlLimits.lcl)}
              stroke="hsl(var(--alarm-critical))"
              strokeWidth={1.5}
              strokeDasharray="4 4"
            />
          )}
          {controlLimits?.mean != null && (
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yFor(controlLimits.mean)}
              y2={yFor(controlLimits.mean)}
              stroke="hsl(var(--state-running))"
              strokeWidth={1.5}
            />
          )}

          {/* Series */}
          {series.map((s, i) => (
            <polyline
              key={s.name}
              points={pointsFor(s.data)}
              fill="none"
              stroke={s.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}

          {/* X-axis labels */}
          {labels && (
            <g>
              {labels.map((label, i) => (
                <text
                  key={i}
                  x={padding.left + i * xStep}
                  y={height - padding.bottom + 15}
                  textAnchor="middle"
                  className="fill-muted-foreground"
                  style={{ fontSize: 10 }}
                >
                  {label}
                </text>
              ))}
            </g>
          )}
        </svg>

        {showLegend && series.length > 1 && (
          <div className="mt-2 flex flex-wrap items-center justify-center gap-4 text-xs">
            {series.map((s, i) => (
              <div key={s.name} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-0.5 w-4"
                  style={{
                    backgroundColor:
                      s.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length],
                  }}
                />
                <span className="text-muted-foreground">{s.name}</span>
              </div>
            ))}
          </div>
        )}

        <ChartDataTable caption="Line chart" series={tableSeries} />
      </div>
    );
  }
);

LineChart.displayName = 'LineChart';
